import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { AlertTriangle, Clock, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useBillingOrders, type BillingOrder } from '@/hooks/use-billing'
import { useAuthStore } from '@/stores/auth-store'
import { useCanBuyAiCredits } from '@/hooks/use-ai-credits'
import { isAiCreditOrder } from './plans'
import { cn } from '@/lib/utils'
import { formatVnd } from '@/lib/format-money'
import { remainingText } from './bank-transfer-panel'
import { OrderActions, orderTitleText } from './order-actions'
import { URGENT_LEFT_MS, methodShortLabel, pickPendingOrders } from './payment-methods'

const DISMISS_KEY = 'billing:barDismissed'

const readDismissed = (): string | null => { try { return sessionStorage.getItem(DISMISS_KEY) } catch { return null } }
const writeDismissed = (id: string) => { try { sessionStorage.setItem(DISMISS_KEY, id) } catch { /* trình duyệt chặn storage — bỏ qua, thanh vẫn hiện lại ở lần tải sau */ } }

/**
 * Đơn chờ thanh toán / vừa hết hạn của tổ chức — dùng cho thanh nhắc toàn cục và chấm báo ở menu. Chủ sở hữu / quản trị thấy mọi
 * đơn; người chỉ có quyền mua lượt AI (`ai_credits.create`) chỉ thấy đơn lượt AI.
 */
export function usePendingOrder(): { pending?: BillingOrder; lapsed?: BillingOrder; now: number } {
  const role = useAuthStore((s) => s.user?.role)
  const canPay = role === 'owner' || role === 'admin'
  const canBuyAi = useCanBuyAiCredits()
  const { data } = useBillingOrders({ enabled: canPay || canBuyAi, staleTime: 60_000 })
  const [now, setNow] = useState(() => Date.now())
  const orders = canPay ? data?.orders : canBuyAi ? data?.orders.filter(isAiCreditOrder) : undefined
  const { pending, lapsed } = orders ? pickPendingOrders(orders, now) : {}
  const ticking = !!(pending || lapsed)
  // Đồng hồ chỉ chạy khi có gì để đếm (không đánh thức ứng dụng mỗi giây vô ích).
  useEffect(() => {
    if (!ticking) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [ticking])
  return { pending, lapsed, now }
}

/**
 * Thanh nhắc "đơn đang chờ thanh toán" ở MỌI menu (trừ các trang /billing — ở đó đã có khung riêng): khách rời trang thanh toán
 * vẫn thấy đơn, tiếp tục / huỷ ngay được. Bấm × → ẩn trong phiên, nhưng còn dưới 5 phút thì hiện lại. Đơn vừa hết hạn → "Tạo mã mới".
 * `mobile`: đặt ngay trên thanh tab dưới, gọn hơn.
 */
export function PendingOrderBar({ mobile = false }: { mobile?: boolean }) {
  const { t } = useTranslation('billing')
  const { pathname } = useLocation()
  const { pending, lapsed, now } = usePendingOrder()
  const [dismissed, setDismissed] = useState<string | null>(readDismissed)
  const order = pending ?? lapsed
  if (!order || pathname.startsWith('/billing')) return null

  const leftMs = pending?.expiresAt ? new Date(pending.expiresAt).getTime() - now : 0
  const urgent = !!pending && leftMs < URGENT_LEFT_MS
  if (dismissed === order.id && !urgent) return null
  const left = pending ? remainingText(pending.expiresAt, now) : null
  const dismiss = () => { writeDismissed(order.id); setDismissed(order.id) }

  return (
    <div
      role="region"
      aria-label={t('pendingBar.region')}
      className={cn(
        // Thẻ trắng như các khung nội dung của ứng dụng (không tô nền màu lên nền shell xanh); mức độ thể hiện bằng viền trái + biểu tượng.
        'flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border border-l-4 bg-background px-3 py-2 text-sm',
        mobile ? 'text-xs' : 'mx-2 mt-2 rounded-xl shadow-sm',
        urgent || lapsed ? 'border-l-destructive' : 'border-l-warning',
      )}
    >
      <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full', urgent || lapsed ? 'bg-destructive/10 text-destructive' : 'bg-warning/15 text-warning')}>
        {lapsed ? <AlertTriangle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
      </span>
      {/* basis 60%: màn hẹp thì các nút xuống hàng riêng thay vì ép chữ thành cột hẹp. */}
      <p className="min-w-0 flex-1 basis-[60%]">
        {lapsed ? (
          <><span className="font-medium">{t('pendingBar.lapsed')}</span> — {orderTitleText(order)} · {formatVnd(order.amount)}</>
        ) : (
          <>
            <span className="font-medium">{t('pendingBar.pending')}</span> — {orderTitleText(order)} · {formatVnd(order.amount)}
            {!mobile && <> · {methodShortLabel(order.provider)}</>}
            {left && <> · {t('pendingBar.leftPrefix')} <span className={cn('font-mono tabular-nums', urgent && 'font-semibold text-destructive')}>{left}</span></>}
          </>
        )}
      </p>
      <OrderActions order={order} />
      <button type="button" onClick={dismiss} className="shrink-0 rounded p-1 text-muted-foreground hover:bg-background/60 hover:text-foreground" aria-label={t('pendingBar.dismiss')}>
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
